import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

export const AlertsView = ({ data, api, authHeaders, onRead }) => {
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestInfo, setDigestInfo] = useState(null);
  const [openDigest, setOpenDigest] = useState(null);
  const [scorecardBusy, setScorecardBusy] = useState(false);
  const [scorecardInfo, setScorecardInfo] = useState(null);
  const [openScorecard, setOpenScorecard] = useState(null);
  const alerts = data?.alerts || [];
  const unread = data?.unread || 0;
  const tgConfigured = data?.telegram_configured;

  const loadDigests = useCallback(async () => {
    try {
      const res = await fetch(`${api}/admin/digests`, { headers: authHeaders });
      if (res.ok) setDigestInfo(await res.json());
      const sres = await fetch(`${api}/admin/scorecards`, { headers: authHeaders });
      if (sres.ok) setScorecardInfo(await sres.json());
    } catch {}
  }, [api, authHeaders.Authorization]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadDigests(); }, [loadDigests]);

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const res = await fetch(`${api}/admin/digest/send`, { method: "POST", headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error("Digest failed");
      setOpenDigest(d.digest);
      toast.success(d.digest.delivered_via === "telegram" ? "Digest sent to your phone via Telegram." : "Digest generated. Delivery is mocked — connect Telegram to get it on your phone.");
      loadDigests();
    } catch {
      toast.error("Couldn't generate the digest. Please try again.");
    }
    setDigestBusy(false);
  };

  const sendScorecard = async () => {
    setScorecardBusy(true);
    try {
      const res = await fetch(`${api}/admin/scorecard/send`, { method: "POST", headers: authHeaders });
      const d = await res.json();
      if (!res.ok) throw new Error("Scorecard failed");
      setOpenScorecard(d.scorecard);
      toast.success(d.scorecard.delivered_via === "telegram" ? "Scorecard sent to your phone." : "Scorecard generated. Delivery is mocked — connect Telegram or Resend to receive it.");
      loadDigests();
    } catch {
      toast.error("Couldn't generate the scorecard. Please try again.");
    }
    setScorecardBusy(false);
  };

  const markRead = async () => {
    setBusy(true);
    try {
      await fetch(`${api}/admin/alerts/read`, { method: "POST", headers: authHeaders });
      onRead();
    } catch {}
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="alerts-view">
      <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-5" data-testid="digest-panel">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div>
            <p className="text-base font-bold text-white">Daily digest</p>
            <p className="text-[15px] font-medium text-[#D6D6D6] mt-0.5">{digestInfo?.schedule || "Daily · 7:00 AM Arizona"} — yesterday's leads, bookings, and busiest conversations</p>
          </div>
          <span data-testid="telegram-status"
            className={`text-[14px] font-semibold border rounded-full px-3 py-1 ${tgConfigured ? "border-[#4ADE80] text-[#4ADE80]" : "border-[#FF5C5C] text-[#FF5C5C]"}`}>
            {tgConfigured ? "Telegram connected" : "Phone delivery off — add Telegram keys"}
          </span>
          <span data-testid="email-status"
            className={`text-[14px] font-semibold border rounded-full px-3 py-1 ${data?.email_configured ? "border-[#4ADE80] text-[#4ADE80]" : "border-[#FF5C5C] text-[#FF5C5C]"}`}>
            {data?.email_configured ? "Email live" : "Email off — add Resend key"}
          </span>
          <span data-testid="sms-status"
            className={`text-[14px] font-semibold border rounded-full px-3 py-1 ${data?.sms_configured ? "border-[#4ADE80] text-[#4ADE80]" : "border-[#FF5C5C] text-[#FF5C5C]"}`}>
            {data?.sms_configured ? "Texts live" : "Texts off — add Twilio keys"}
          </span>
          <button onClick={sendDigest} disabled={digestBusy} data-testid="send-digest-button"
            className="ml-auto flex items-center gap-2 text-[15px] font-semibold rounded-xl bg-white text-black px-4 py-2.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
            {digestBusy ? "Building..." : "Send digest now"}
          </button>
        </div>
        {!tgConfigured && (
          <p className="text-[15px] font-medium text-[#D6D6D6] mt-3 leading-relaxed">
            To get alerts and digests on your phone: create a bot via @BotFather on Telegram, grab your chat ID from @userinfobot, then set TELEGRAM_BOT_TOKEN and TELEGRAM_CHAT_ID in backend/.env.
          </p>
        )}
        {!data?.email_configured && (
          <p className="text-[15px] font-medium text-[#D6D6D6] mt-2 leading-relaxed">
            To get alerts, digests, and lead follow-ups by real email: grab an API key at resend.com/api-keys, then set RESEND_API_KEY and ALERT_EMAIL (your inbox) in backend/.env. Everything is wired — it goes live the moment the keys land.
          </p>
        )}
        {(openDigest || digestInfo?.digests?.[0]) && (
          <pre data-testid="digest-preview"
            className="mt-4 font-sans text-[15px] font-medium text-[#D6D6D6] leading-relaxed whitespace-pre-wrap border-t border-[#2E2E2E] pt-4">
            {(openDigest || digestInfo.digests[0]).text}
          </pre>
        )}
      </div>

      <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-5" data-testid="scorecard-panel">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div>
            <p className="text-base font-bold text-white">Weekly scorecard</p>
            <p className="text-[15px] font-medium text-[#D6D6D6] mt-0.5">{scorecardInfo?.schedule || "Mondays · 7:30 AM Arizona"} — last week against the week before: leads, bookings, closes</p>
          </div>
          <button onClick={sendScorecard} disabled={scorecardBusy} data-testid="send-scorecard-button"
            className="ml-auto flex items-center gap-2 text-[15px] font-semibold rounded-xl bg-white text-black px-4 py-2.5 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
            {scorecardBusy ? "Building..." : "Send scorecard now"}
          </button>
        </div>
        {(openScorecard || scorecardInfo?.scorecards?.[0]) && (
          <pre data-testid="scorecard-preview"
            className="mt-4 font-sans text-[15px] font-medium text-[#D6D6D6] leading-relaxed whitespace-pre-wrap border-t border-[#2E2E2E] pt-4">
            {(openScorecard || scorecardInfo.scorecards[0]).text}
          </pre>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-base font-semibold text-[#D6D6D6]" data-testid="alerts-unread-count">
          {unread > 0 ? `${unread} unread` : "All caught up"}
        </p>
        {unread > 0 && (
          <button onClick={markRead} disabled={busy} data-testid="alerts-mark-read-button"
            className="flex items-center gap-2 text-[15px] font-semibold rounded-xl border border-[#2E2E2E] px-4 py-2.5 text-[#D6D6D6] hover:bg-white hover:text-black disabled:opacity-40 transition-colors duration-150">
            Mark all read
          </button>
        )}
      </div>
      {alerts.length === 0 && (
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-10 text-center" data-testid="alerts-empty">
          <p className="text-base font-medium text-[#D6D6D6]">No lead alerts yet. When a serious lead verifies or books a call, it lands here first.</p>
        </div>
      )}
      <div className="space-y-3">
        {alerts.map((a) => {
          return (
            <div key={a.id} data-testid={`alert-item-${a.kind}`}
              className={`flex items-start gap-4 rounded-2xl border bg-[#111111] p-5 ${a.read ? "border-[#2E2E2E]" : "border-l-4 border-l-[#DC2626] border-[#2E2E2E]"}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <p className={`text-base font-semibold ${a.read ? "text-[#D6D6D6]" : "text-white"}`}>{a.title}</p>
                  {!a.read && <span className="text-[14px] font-bold text-[#FF5C5C]">New</span>}
                </div>
                <p className="text-[15px] font-medium text-[#D6D6D6] mt-1 break-words">{a.body}</p>
                {a.meta?.visitor_sid && (
                  <a href={`/admin?visitor=${a.meta.visitor_sid}`} data-testid="alert-visitor-link"
                    className="inline-block text-[15px] font-semibold text-[#FF5C5C] hover:text-white mt-2 transition-colors duration-150">
                    See their activity →
                  </a>
                )}
                <p className="text-[14px] font-medium text-[#D6D6D6] mt-2">
                  {new Date(a.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
